/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, useParams, Link } from 'react-router-dom';
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import PauseAlertImpactDetailCards from "./PauseAlertImpactDetailCards";
import PauseAlertImpactChart from "./PauseAlertImpactChart";
import PauseAlertImpactBreathTable from "./PauseAlertImpactBreathTable";
import "../active-users/active-users.css";
import "./PauseAlertImpact.css";
import { formatUTCDate } from '../../utils/utils';

const TREND_LABEL = {
  decreased: 'Decreased',
  increased: 'Increased',
  unchanged: 'No change',
  no_baseline: 'No baseline',
};

// Guarded decode. atob throws on malformed base64, and an uncaught throw during
// render blanks the page — StandingCheckDetailList does not guard this, which is
// not a pattern worth inheriting. Mirrors subscription/History.js.
const decodeUserId = (encoded) => {
  try {
    return atob(encoded || '');
  } catch {
    return '';
  }
};

const PauseAlertImpactDetail = () => {
  const navigate = useNavigate();
  const { encodedUserId } = useParams();
  const userId = decodeUserId(encodedUserId);

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [facts, setFacts] = useState(null);
  const [span, setSpan] = useState(null);
  const [series, setSeries] = useState([]);
  const [truncated, setTruncated] = useState(false);

  // Individual breath records behind the breath_sessions count.
  const BREATH_LIMIT = 20;
  const [breathRows, setBreathRows] = useState([]);
  const [breathTotal, setBreathTotal] = useState(0);
  const [breathPage, setBreathPage] = useState(1);
  const [breathLoading, setBreathLoading] = useState(true);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => { refreshToken(); }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => { if (token) load(); }, [token]);

  const load = async () => {
    // A malformed or absent id never reaches the API — render the empty state.
    if (!userId) { setLoading(false); return; }
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(
        `${baseUrl}/admin/api/pause-alert-impact-details?user_id=${encodeURIComponent(userId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const d = res.data.data || {};
      setFacts(d.facts || null);
      setSpan(d.span || null);
      setSeries(d.series || []);
      setTruncated(!!d.truncated);
      // Only fetch records once we know the user qualifies.
      if (d.facts) loadBreath(1);
      else setBreathLoading(false);
    } catch (err) {
      setError(err?.response?.status === 401 ? 'auth' : 'load');
    } finally {
      setLoading(false);
    }
  };

  const loadBreath = async (p) => {
    if (!userId) return;
    setBreathLoading(true);
    try {
      const res = await axiosJWT.get(
        `${baseUrl}/admin/api/pause-alert-impact-breath?user_id=${encodeURIComponent(userId)}` +
        `&page=${p}&limit=${BREATH_LIMIT}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBreathRows(res.data.data || []);
      setBreathTotal(res.data.totalItem || 0);
      setBreathPage(p);
    } catch {
      // A failure here must not blank the whole page — the summary above is still valid.
      setBreathRows([]);
    } finally {
      setBreathLoading(false);
    }
  };

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Pause → alert impact › <b>Details</b></span>
            <span className="au-route">/pause-alert-impact-details</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/pause-alert-impact">← Back</Link></span>
          </div>
          <h1 className="au-title">
            Pause → Alert Impact
            {facts?.email ? <span className="au-subtitle"> — {facts.email}</span> : null}
          </h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load this user’s detail. Please try again." onRetry={load} />
          ) : loading ? (
            <div className="au-panel"><TableSkeleton rows={6} /></div>
          ) : !facts ? (
            <EmptyState
              title="Nothing to show for this user"
              icon="🫁"
              message="This user does not have breath sessions spanning at least 7 days, so there is no pause window to measure against."
              hasFilters={false}
            />
          ) : (
            <>
              <p className="au-range-note pai-scope-note" data-testid="pai-detail-scope-note">
                A <b>pause</b> here means a <b>breath session only</b> — meditation is excluded.
                An <b>alert</b> is a heart-rate session. This user’s pause window ran{' '}
                <b>{formatUTCDate(facts.pause_start)}</b> → <b>{formatUTCDate(facts.pause_end)}</b>, compared against the 30 days
                immediately before it.
              </p>

              <PauseAlertImpactDetailCards facts={facts} />

              <PauseAlertImpactChart
                series={series}
                span={span}
                truncated={truncated}
                baselineRate={Number(facts.alerts_per_day_before) || 0}
              />

              <PauseAlertImpactBreathTable
                rows={breathRows}
                totalItem={breathTotal}
                expectedTotal={facts.breath_sessions}
                page={breathPage}
                limit={BREATH_LIMIT}
                loading={breathLoading}
                onPageChange={loadBreath}
              />

              <div className="au-panel">
                <div className="au-panel__head">
                  <h4 className="au-panel__title">Per-user alert impact</h4>
                </div>
                <div className="au-table-wrap">
                  <table className="au-table pai-table" data-testid="pai-detail-table">
                    <thead>
                      <tr>
                        <th>Email</th><th>Registered</th><th>Last Active (UTC)</th>
                        <th>Breath Sessions</th><th>Pause Start</th><th>Pause End</th>
                        <th>Alerts Before</th><th>Alerts During</th>
                        <th>Alerts/Day Before</th><th>Alerts/Day During</th>
                        <th>Rate Delta</th><th>Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td data-label="Email" className="au-ellipsis">{facts.email}</td>
                        <td data-label="Registered">{formatUTCDate(facts.register_time) || '-'}</td>
                        <td data-label="Last Active (UTC)" className={facts.last_active ? '' : 'pai-never'}>
                          {formatUTCDate(facts.last_active) || 'Never'}
                        </td>
                        <td data-label="Breath Sessions" className="tnum">{facts.breath_sessions}</td>
                        <td data-label="Pause Start">{formatUTCDate(facts.pause_start)}</td>
                        <td data-label="Pause End">{formatUTCDate(facts.pause_end)}</td>
                        <td data-label="Alerts Before" className="tnum">{facts.alerts_before}</td>
                        <td data-label="Alerts During" className="tnum">{facts.alerts_during}</td>
                        <td data-label="Alerts/Day Before" className="tnum">{facts.alerts_per_day_before}</td>
                        <td data-label="Alerts/Day During" className="tnum">{facts.alerts_per_day_during}</td>
                        <td
                          data-label="Rate Delta"
                          className={`tnum ${Number(facts.rate_delta) < 0 ? 'pai-delta-down' : Number(facts.rate_delta) > 0 ? 'pai-delta-up' : ''}`}
                        >
                          {facts.rate_delta}
                        </td>
                        <td data-label="Trend">
                          <span
                            className={`pai-badge pai-badge--${facts.alert_trend}`}
                            data-testid={`pai-detail-trend-${facts.user_id}`}
                          >
                            {TREND_LABEL[facts.alert_trend] || facts.alert_trend}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PauseAlertImpactDetail;
